import { useContext, useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import LanguageContext from "../components/LanguageContext";

function Services() {
  const { t } = useContext(LanguageContext);
  const [selected, setSelected] = useState(null);
  const closeModal = () => setSelected(null);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === "Escape") closeModal();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  useEffect(() => {
    if (selected) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [selected]);

  const priceLines = useMemo(() => {
    if (!selected) return [];
    const s = selected;
    if (Array.isArray(s.priceLines)) return s.priceLines;

    if (typeof s.price === "string" && s.price.trim()) {
      return s.price
        .split(/\n|·|\/|•/g)
        .map((x) => x.trim())
        .filter(Boolean);
    }

    return [];
  }, [selected]);

  const benefits = useMemo(() => {
    if (!selected) return [];
    const s = selected;

    if (Array.isArray(s.benefits) && s.benefits.length) return s.benefits;

    if (typeof s.desc === "string" && s.desc.trim()) {
      return s.desc
        .split(/。|\.|；|;/)
        .map((x) => x.trim())
        .filter(Boolean)
        .slice(0, 4);
    }

    return [];
  }, [selected]);

  return (
    <motion.div className="page">
      <h2 className="section-title">{t.servicesTitle}</h2>
      <p className="section-intro">{t.servicesIntro}</p>

      <motion.div 
      className="services-grid"
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.1}}
      variants={{
        hidden:{},
        visible:{transition : {staggerChildren: 0.12}}
      }}
      >
        {t.services.map((s, idx) => (
          <motion.article
            key={idx}
            className="service-card"
            variants={{
              hidden:{opacity:0, y:30},
              visible:{opacity:1, y:0, transition:{duration:0.5, ease:'easeOut'}}
            }}
            role="button"
            tabIndex={0}
            onClick={() => setSelected(s)}
            onKeyDown={(e) => e.key === "Enter" && setSelected(s)}
          >
            {s.image && <img src={s.image} alt={s.name} className="service-card-image" />}

            <div className="service-card-overlay">
              <h3 className="service-card-name">{s.name}</h3>

              {s.minPrice && (
                <div className="service-card-minprice">{s.minPrice}</div>
              )}
            </div>
          </motion.article>
        ))}
      </motion.div>

      <AnimatePresence>
        {selected && (
          <motion.div
            className="modal-backdrop"
            onClick={closeModal}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="modal"
              onClick={(e) => e.stopPropagation()} // prevent closing when clicking inside
              initial={{ opacity: 0, y: 10, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 10, scale: 0.98 }}
              transition={{ duration: 0.25 }}
            >
              <button className="modal-close" onClick={closeModal} aria-label="Close">
                ✕
              </button>

              <h3 className="modal-title">{selected.name}</h3>
              {selected.minPrice && <div className="modal-sub">{selected.minPrice}</div>}

              <div className="modal-section">
                <div className="modal-label">{t.modalPriceTitle}</div>
                <ul className="modal-benefits">
                  {priceLines.map((p, i) => (
                    <li key={i}>{p}</li>
                  ))}
                </ul>
              </div>

              <div className="modal-section">
                <div className="modal-label">{t.modalDescTitle}</div>
                {selected.desc}
              </div>

              <div className="modal-section">
                <div className="modal-label">{t.modalBenefitsTitle}</div>
                <ul className="modal-benefits">
                  {benefits.map((b, i) => (
                    <li key={i}>{b}</li>
                  ))}
                </ul>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

export default Services;
